import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import type { StrategicPhase } from '@/hooks/useStrategicStream';

// ============================================================================
// PHASE DEFINITIONS
// ============================================================================

interface PhaseSegment {
  id: StrategicPhase | 'debate_mediation';
  label: string;
  shortLabel: string;
  /** Relative width weight (wider = more visual prominence) */
  weight: number;
  /** Phases from StrategicPhase that map to this segment */
  matchPhases: StrategicPhase[];
}

const PHASE_SEGMENTS: PhaseSegment[] = [
  { id: 'planning',            label: 'Plan',       shortLabel: 'P', weight: 1, matchPhases: ['planning'] },
  { id: 'engagement_manager',  label: 'Engage',     shortLabel: 'E', weight: 1, matchPhases: ['engagement_manager'] },
  { id: 'data_anchoring',      label: 'Anchor',     shortLabel: 'A', weight: 1, matchPhases: ['data_anchoring'] },
  { id: 'workstreams',         label: 'Research',   shortLabel: 'R', weight: 2, matchPhases: ['workstreams'] },
  { id: 'synthesis',           label: 'Synth',      shortLabel: 'S', weight: 1, matchPhases: ['synthesis'] },
  { id: 'stress_test',         label: 'Stress',     shortLabel: 'T', weight: 1, matchPhases: ['stress_test'] },
  { id: 'debate_mediation',    label: 'Debate',     shortLabel: 'D', weight: 2, matchPhases: ['debate', 'mediation'] },
  { id: 'aggregation',         label: 'Final',      shortLabel: 'F', weight: 1, matchPhases: ['aggregation'] },
];

const TOTAL_WEIGHT = PHASE_SEGMENTS.reduce((sum, s) => sum + s.weight, 0);

// ============================================================================
// HELPERS
// ============================================================================

/** Returns the index of the segment that the current phase belongs to, or -1 for idle/error. */
function getActiveSegmentIndex(phase: StrategicPhase): number {
  if (phase === 'idle' || phase === 'error') return -1;
  if (phase === 'concluded') return PHASE_SEGMENTS.length; // all complete
  return PHASE_SEGMENTS.findIndex((s) => s.matchPhases.includes(phase));
}

function getPhaseDisplayName(phase: StrategicPhase): string {
  switch (phase) {
    case 'planning':           return 'Planning';
    case 'engagement_manager': return 'Engagement Manager';
    case 'data_anchoring':     return 'Data Anchoring';
    case 'workstreams':        return 'Workstream Research';
    case 'synthesis':          return 'Synthesis';
    case 'stress_test':        return 'Stress Testing';
    case 'debate':             return 'Debate';
    case 'mediation':          return 'Mediation';
    case 'aggregation':        return 'Aggregation';
    case 'concluded':          return 'Complete';
    default:                   return '';
  }
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ============================================================================
// COMPONENT
// ============================================================================

interface PhaseProgressBarProps {
  phase: StrategicPhase;
  isSmallScreen: boolean;
  startedAt?: string;
}

export function PhaseProgressBar({ phase, isSmallScreen, startedAt }: PhaseProgressBarProps) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) {
      setElapsed(0);
      return;
    }

    const start = new Date(startedAt).getTime();

    function tick() {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const activeIdx = getActiveSegmentIndex(phase);
  const isRunning = phase !== 'idle' && phase !== 'concluded' && phase !== 'error';
  const phaseNumber = activeIdx >= 0 ? Math.min(activeIdx + 1, PHASE_SEGMENTS.length) : 0;

  return (
    <div className="w-full space-y-1.5">
      {/* Segmented bar */}
      <div className="flex gap-0.5 h-2 rounded-full overflow-hidden bg-surface">
        {PHASE_SEGMENTS.map((segment, idx) => {
          const widthPercent = (segment.weight / TOTAL_WEIGHT) * 100;
          const isComplete = idx < activeIdx;
          const isActive = idx === activeIdx;

          return (
            <div
              key={segment.id}
              className={cn(
                'transition-colors duration-500 first:rounded-l-full last:rounded-r-full',
                isComplete && 'bg-verdict-primary',
                isActive && 'bg-verdict-primary animate-pulse',
                !isComplete && !isActive && 'bg-surface-light',
              )}
              style={{ width: `${widthPercent}%` }}
              title={segment.label}
            />
          );
        })}
      </div>

      {/* Labels row */}
      <div className="flex gap-0.5">
        {PHASE_SEGMENTS.map((segment, idx) => {
          const widthPercent = (segment.weight / TOTAL_WEIGHT) * 100;
          const isComplete = idx < activeIdx;
          const isActive = idx === activeIdx;

          return (
            <div
              key={segment.id}
              className={cn(
                'text-center truncate leading-none',
                isSmallScreen ? 'text-[9px]' : 'text-[10px]',
                isComplete && 'text-verdict-primary/70',
                isActive && 'text-verdict-primary font-semibold',
                !isComplete && !isActive && 'text-text-muted',
              )}
              style={{ width: `${widthPercent}%` }}
            >
              {isSmallScreen ? segment.shortLabel : segment.label}
            </div>
          );
        })}
      </div>

      {/* Status line */}
      {isRunning && (
        <div className={cn(
          'flex items-center justify-between text-text-muted font-mono',
          isSmallScreen ? 'text-[10px]' : 'text-xs',
        )}>
          <span>
            Phase {phaseNumber} of {PHASE_SEGMENTS.length}
            <span className="text-text-muted"> &mdash; </span>
            <span className="text-text-secondary">{getPhaseDisplayName(phase)}</span>
          </span>
          {startedAt && (
            <span className="flex items-center gap-1 text-text-muted">
              <Clock className="w-3 h-3" />
              {formatElapsed(elapsed)}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
